// src/b2b/components/FormulaireSejour.jsx
import { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import PriceCalendarModal from './PriceCalendarModal';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// helpers
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');
const norm = (x) =>
  String(x ?? '')
    .toUpperCase()
    .replace(/^_?LT_/, '');
const toInputDate = (raw) => {
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};
const addDays = (d, n) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
};

function FormulaireSejour({ produit, onSubmit }) {
  // --- Froms + bestFrom par défaut
  const froms = produit?.froms ?? produit?.Froms ?? [];
  const bestFromCode =
    produit?.bestPrice?.bestPrice?._$From ?? produit?.bestPrice?._$From ?? '';
  const matchedFrom = useMemo(
    () =>
      froms.find(
        (f) => norm(f?.id) === norm(bestFromCode) || norm(f?.title) === norm(bestFromCode),
      ),
    [froms, bestFromCode],
  );

  // label affiché dans le select (capitalisé)
  const initialFromTitle = matchedFrom?.title || froms?.[0]?.title || '';
  const [fromTitle, setFromTitle] = useState(initialFromTitle);

  // code technique du from (IATA/PGI) dérivé du titre sélectionné
  const fromCode = useMemo(() => {
    const found = froms.find((f) => f.title === fromTitle);
    return found?.id || matchedFrom?.id || froms?.[0]?.id || '';
  }, [froms, fromTitle, matchedFrom?.id]);

  useEffect(() => {
    setFromTitle(initialFromTitle);
  }, [initialFromTitle]);

  // --- Produit/code
  const productCode = produit?._$id || produit?.id || '';

  // --- Durées disponibles (fallback 4j/3n -> 22j/21n)
  const fallbackDurations = useMemo(
    () =>
      Array.from({ length: 19 }, (_, i) => {
        const days = i + 4;
        return { days, nights: days - 1 };
      }),
    [],
  );

  const [durations, setDurations] = useState(fallbackDurations);
  const [loadingDur, setLoadingDur] = useState(false);

  // durée sélectionnée (essaie de coller à produit.information.Night)
  const defaultNights = Number(produit?.information?.Night ?? 3);
  const findIdx = (arr, nights) =>
    Math.max(
      0,
      arr.findIndex((d) => d.nights === nights),
    );
  const [selectedDuree, setSelectedDuree] = useState(
    fallbackDurations[findIdx(fallbackDurations, defaultNights)] || fallbackDurations[0],
  );

  // fetch des durées réelles quand code/from change
  useEffect(() => {
    let mounted = true;
    if (!productCode || !fromCode) return;

    setLoadingDur(true);
    axios
      .get(`/api/prices/available-durations`, {
        params: { code: productCode, from: fromCode },
        timeout: 15000,
      })
      .then((res) => {
        if (!mounted) return;
        const avail = Array.isArray(res.data?.durations) ? res.data.durations : [];
        if (avail.length > 0) {
          setDurations(avail);
          const idx = findIdx(avail, selectedDuree?.nights ?? defaultNights);
          setSelectedDuree(avail[idx] || avail[0]);
        } else {
          setDurations(fallbackDurations);
          const idx = findIdx(fallbackDurations, selectedDuree?.nights ?? defaultNights);
          setSelectedDuree(fallbackDurations[idx] || fallbackDurations[0]);
        }
      })
      .catch(() => {
        if (!mounted) return;
        setDurations(fallbackDurations);
        const idx = findIdx(fallbackDurations, selectedDuree?.nights ?? defaultNights);
        setSelectedDuree(fallbackDurations[idx] || fallbackDurations[0]);
      })
      .finally(() => mounted && setLoadingDur(false));

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode, fromCode]);

  // --- Date & voyageurs
  const [date, setDate] = useState(''); // yyyy-mm-dd
  const [displayedPrice, setDisplayedPrice] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [openCal, setOpenCal] = useState(false);

  // recalcul date de fin
  const endDate = useMemo(() => {
    if (!date || !selectedDuree?.nights) return '';
    return toInputDate(addDays(date, selectedDuree.nights));
  }, [date, selectedDuree]);

  // quand on change la durée ou la ville, on reset la date/prix (le calendrier va refetch)
  useEffect(() => {
    setDate('');
    setDisplayedPrice(null);
  }, [selectedDuree, fromCode]);

  function fmt(d) {
    try {
      return new Date(d).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return d;
    }
  }

  function handlePick({ dateISO, price }) {
    setDate(toInputDate(dateISO));
    setDisplayedPrice(price);
    setOpenCal(false);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      nights: selectedDuree?.nights,
      days: selectedDuree?.days,
      from: fromTitle, // valeur “humaine”
      fromCode, // valeur technique (utile côté API si besoin)
      date,
      endDate,
      adults,
      children,
      productId: produit?._id || produit?.id,
      productName: produit?.description?.name,
      startsAt: date,
      displayedPriceCents:
        displayedPrice != null ? Math.round(Number(displayedPrice) * 100) : null,
    };
    if (onSubmit) onSubmit(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="p-3 border rounded bg-light">
      <h3 className="mb-3 text-center" style={{ color: '#f56371' }}>
        Réserver ce séjour
      </h3>

      {/* Ville de départ */}
      <div className="mb-3">
        <label className="form-label">Ville de départ</label>
        <select
          className="form-control"
          value={fromTitle}
          onChange={(e) => setFromTitle(e.target.value)}
        >
          {froms.map((f, i) => (
            <option key={i} value={f.title}>
              {capFirst(f.title)}
            </option>
          ))}
        </select>
      </div>

      {/* Durée */}
      <div className="mb-3">
        <label className="form-label">
          Durée {loadingDur && <small className="text-muted"> (chargement…)</small>}
        </label>
        <select
          className="form-control"
          value={`${selectedDuree.days}-${selectedDuree.nights}`}
          onChange={(e) => {
            const [d, n] = e.target.value.split('-').map(Number);
            setSelectedDuree({ days: d, nights: n });
          }}
        >
          {durations.map((d, i) => (
            <option key={`${d.days}-${d.nights}-${i}`} value={`${d.days}-${d.nights}`}>
              {d.days} jours {d.nights} nuits
            </option>
          ))}
        </select>
        {!!date && !!endDate && (
          <small className="text-muted">
            Séjour du <strong>{fmt(date)}</strong> au <strong>{fmt(endDate)}</strong> (
            {selectedDuree.nights} nuit{selectedDuree.nights > 1 ? 's' : ''})
          </small>
        )}
      </div>

      {/* Date de départ (ouvre le calendrier) */}
      <div className="mb-12">
        <label className="form-label">Date de départ</label>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setOpenCal(true)}
            style={{ width: '100%' }}
          >
            {date ? new Date(date).toLocaleDateString('fr-FR') : 'Choisir une date'}
          </button>
        </div>
      </div>

      {/* Voyageurs */}
      <div className="row g-2 mb-3">
        <div className="col">
          <label className="form-label">Adultes</label>
          <input
            type="number"
            min={1}
            className="form-control"
            value={adults}
            onChange={(e) => setAdults(Number(e.target.value))}
          />
        </div>
        <div className="col">
          <label className="form-label">Enfants</label>
          <input
            type="number"
            min={0}
            className="form-control"
            value={children}
            onChange={(e) => setChildren(Number(e.target.value))}
          />
        </div>
      </div>

      {displayedPrice != null && (
        <div className="text-center m-3" style={{ color: '#f56371' }}>
          <strong>{Number(displayedPrice).toFixed(2)} €</strong> / Pers
          <br />
          <strong>{(Number(displayedPrice) * adults).toFixed(2)} €</strong> Total
        </div>
      )}

      <button type="submit" className="btn btn-primary w-100" disabled={!date}>
        Réserver
      </button>

      {/* Modal calendrier : on lui passe le fromCode et les nuits choisies */}
      <PriceCalendarModal
        isOpen={openCal}
        onClose={() => setOpenCal(false)}
        onSelect={handlePick}
        productCode={productCode}
        defaultFrom={fromCode}
        nights={selectedDuree?.nights || 3}
      />
    </form>
  );
}

export default FormulaireSejour;
